/**
 * System Health error reporter (spec §10, SH-50–52). It sends uncaught errors, unhandled
 * rejections and failed API calls (5xx and network failures, never 4xx) to the backend's public
 * client-errors endpoint, where they land in the HQ error log.
 *
 * It is passive: every entry point swallows its own failures, so nothing here can throw into the
 * app or change the result of a wrapped API call (S9). This is the Admin port of the Hub's reporter
 * (Hub commit ee84a6dd); the Hub, Admin and supplier-portal copies differ only in the `app`,
 * `endpoint`, `getRegion` and `releaseTag` that `src/index.tsx` passes in.
 *
 * Delivery: an in-memory queue flushed every 5 s or at 10 items, by `fetch` with `keepalive` and
 * the bearer token when there is one. On `pagehide` whatever is left goes by `sendBeacon` as a
 * text/plain string, which needs no preflight and carries no auth. A failed send is retried once,
 * 10 s later, then dropped. The same fingerprint is dropped within 60 s, and at most 20 reports
 * leave a tab per rolling minute. No email and no request body is ever sent.
 *
 * Every body, fetch or beacon, stays at or under 48 KB of UTF-8: the backend refuses a body over
 * 64 KB, and the browser caps keepalive and beacon bodies at 64 KB too. A flush is split into as
 * many bodies as that takes, and a report too big to fit alone is cut, its stack first.
 */

export type ReporterApp = "HUB" | "ADMIN" | "SUPPLIER";

export interface ReporterConfig {
  app: ReporterApp;
  endpoint: string;
  getRegion: () => string | null;
  getToken: () => Promise<string | null>;
  releaseTag: string;
  now?: () => number;
  fetchImpl?: typeof fetch;
  sendBeacon?: (url: string, body: string) => boolean;
  setTimer?: (fn: () => void, ms: number) => unknown;
}

type ReportKind = "JS_ERROR" | "UNHANDLED_REJECTION" | "API_5XX" | "NETWORK";

interface ClientErrorReport {
  app: ReporterApp;
  region: string | null;
  kind: ReportKind;
  message: string;
  stack?: string;
  page: string | null;
  apiPath?: string;
  httpStatus?: number;
  releaseTag: string;
  occurredAt: string;
}

const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_AT = 10;
const RETRY_AFTER_MS = 10_000;
const DEDUPE_WINDOW_MS = 60_000;
const MAX_PER_MINUTE = 20;
const MAX_MESSAGE = 1000;
const MAX_STACK = 8000;
const NEVER_REPORT = "/client-errors";
const MAX_BODY_BYTES = 48 * 1024;
/** `{"reports":[]}`: the body around the reports, which are joined by one-byte commas. */
const ENVELOPE_BYTES = 14;

interface ReporterState {
  cfg: ReporterConfig;
  queue: ClientErrorReport[];
  /** fingerprint → when it was last accepted. */
  seen: Map<string, number>;
  /** when each of the last minute's reports was accepted. */
  accepted: number[];
  timerArmed: boolean;
  listeners: [string, EventListener][];
}

let state: ReporterState | null = null;

const now = (s: ReporterState) => (s.cfg.now ? s.cfg.now() : Date.now());

const setTimer = (s: ReporterState, fn: () => void, ms: number) => {
  if (s.cfg.setTimer) s.cfg.setTimer(fn, ms);
  else setTimeout(fn, ms);
};

/** The path alone: the backend strips the query string too, but it never needs to leave the tab. */
const pathOnly = (value: string) => value.split(/[?#]/)[0];

const firstFrame = (stack: string | undefined) => {
  if (!stack) return "";
  const frame = stack.split("\n").find((line) => /^\s*at\s|@/.test(line));
  return frame ? frame.trim() : "";
};

const fingerprint = (r: ClientErrorReport) =>
  [r.kind, r.message.slice(0, 200), r.apiPath ?? "", firstFrame(r.stack)].join("|");

const currentPage = () => {
  try {
    return typeof window === "undefined" ? null : window.location.pathname;
  } catch {
    return null;
  }
};

const safeRegion = (s: ReporterState) => {
  try {
    return s.cfg.getRegion();
  } catch {
    return null;
  }
};

const safeToken = async (s: ReporterState) => {
  try {
    return await s.cfg.getToken();
  } catch {
    return null;
  }
};

/** UTF-8 length without TextEncoder, which the Admin's jsdom lacks. A lone surrogate counts 3, as it encodes. */
const utf8Bytes = (text: string) => {
  let n = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff && (text.charCodeAt(i + 1) & 0xfc00) === 0xdc00) {
      n += 4;
      i++;
    } else n += 3;
  }
  return n;
};

const reportBytes = (r: ClientErrorReport) => utf8Bytes(JSON.stringify(r));

/** The fields a report too big for one body gives up, in order: the stack first. */
const CUTTABLE = ["stack", "apiPath", "page", "message"] as const;

/** The first `length` chars of `value`, less a final high surrogate whose pair the cut would split. */
const prefix = (value: string, length: number) =>
  length > 0 && (value.charCodeAt(length - 1) & 0xfc00) === 0xd800 ? value.slice(0, length - 1) : value.slice(0, length);

/** A report that fits in a body of its own, cut from the end of each field in turn if it must be. */
const fitReport = (r: ClientErrorReport): ClientErrorReport => {
  const fits = (report: ClientErrorReport) => ENVELOPE_BYTES + reportBytes(report) <= MAX_BODY_BYTES;
  if (fits(r)) return r;
  const fitted: ClientErrorReport = { ...r };
  const fields = fitted as unknown as Record<string, string | null | undefined>;
  for (const field of CUTTABLE) {
    const value = fields[field];
    if (typeof value !== "string" || value.length === 0 || fits(fitted)) continue;
    // Binary search for the longest prefix that fits, measured in encoded bytes: a char costs
    // 1 to 6 of them once JSON-escaped, so a cut counted in chars throws away far too much. The
    // body only grows with the prefix, and the whole value is already known not to fit.
    let lo = 0;
    let hi = value.length - 1;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      fields[field] = prefix(value, mid);
      if (fits(fitted)) lo = mid;
      else hi = mid - 1;
    }
    fields[field] = prefix(value, lo);
    if (field === "stack" && fitted.stack === "") delete fitted.stack;
  }
  return fitted;
};

/** Splits reports into bodies of at most MAX_BODY_BYTES, keeping their order. */
const toBatches = (reports: ClientErrorReport[]): ClientErrorReport[][] => {
  const batches: ClientErrorReport[][] = [];
  let current: ClientErrorReport[] = [];
  let size = ENVELOPE_BYTES;
  for (const report of reports) {
    const fitted = fitReport(report);
    const bytes = reportBytes(fitted);
    if (current.length > 0 && size + 1 + bytes > MAX_BODY_BYTES) {
      batches.push(current);
      current = [];
      size = ENVELOPE_BYTES;
    }
    size += (current.length > 0 ? 1 : 0) + bytes;
    current.push(fitted);
  }
  if (current.length > 0) batches.push(current);
  return batches;
};

const send = async (s: ReporterState, batch: ClientErrorReport[], attempt: number) => {
  try {
    const token = await safeToken(s);
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const doFetch = s.cfg.fetchImpl ?? fetch;
    const response = await doFetch(s.cfg.endpoint, {
      method: "POST",
      keepalive: true,
      headers,
      body: JSON.stringify({ reports: batch }),
    });
    if (!response.ok) throw new Error(`client-errors answered ${response.status}`);
  } catch {
    if (attempt === 0 && state === s) {
      try {
        setTimer(s, () => void send(s, batch, 1), RETRY_AFTER_MS);
      } catch {
        // dropped
      }
    }
  }
};

const flush = (s: ReporterState) => {
  try {
    if (s.queue.length === 0) return;
    const batches = toBatches(s.queue.splice(0));
    // One body in flight at a time, so two near-cap bodies never share the keepalive quota.
    void (async () => {
      for (const batch of batches) await send(s, batch, 0);
    })();
  } catch {
    // dropped
  }
};

const enqueue = (report: Omit<ClientErrorReport, "app" | "region" | "page" | "releaseTag" | "occurredAt">) => {
  const s = state;
  if (!s) return;
  try {
    if (report.apiPath?.includes(NEVER_REPORT)) return;
    const full: ClientErrorReport = {
      app: s.cfg.app,
      region: safeRegion(s),
      kind: report.kind,
      message: (report.message || "(no message)").slice(0, MAX_MESSAGE),
      page: currentPage(),
      releaseTag: s.cfg.releaseTag,
      occurredAt: new Date(now(s)).toISOString(),
    };
    if (report.stack) full.stack = report.stack.slice(0, MAX_STACK);
    if (report.apiPath !== undefined) full.apiPath = report.apiPath;
    if (report.httpStatus !== undefined) full.httpStatus = report.httpStatus;

    const t = now(s);
    // forEach, not for…of: admin-ui compiles to ES5 without downlevelIteration (TS2802).
    s.seen.forEach((at, key) => {
      if (t - at >= DEDUPE_WINDOW_MS) s.seen.delete(key);
    });
    const fp = fingerprint(full);
    if (s.seen.has(fp)) return;
    s.accepted = s.accepted.filter((at) => t - at < DEDUPE_WINDOW_MS);
    if (s.accepted.length >= MAX_PER_MINUTE) return;
    s.seen.set(fp, t);
    s.accepted.push(t);

    s.queue.push(full);
    if (s.queue.length >= FLUSH_AT) {
      flush(s);
    } else if (!s.timerArmed) {
      s.timerArmed = true;
      setTimer(
        s,
        () => {
          s.timerArmed = false;
          flush(s);
        },
        FLUSH_INTERVAL_MS
      );
    }
  } catch {
    // dropped
  }
};

const describeReason = (reason: unknown): { message: string; stack?: string } => {
  if (reason instanceof Error) return { message: reason.message || reason.name, stack: reason.stack };
  if (typeof reason === "string") return { message: reason };
  try {
    return { message: String(reason) };
  } catch {
    return { message: "(unprintable rejection)" };
  }
};

/** Installs the window listeners. Idempotent, and never throws. */
export function installErrorReporter(cfg: ReporterConfig): void {
  try {
    if (state || typeof window === "undefined") return;
    const s: ReporterState = { cfg, queue: [], seen: new Map(), accepted: [], timerArmed: false, listeners: [] };
    state = s;

    const onError = (event: Event) => {
      try {
        const e = event as ErrorEvent;
        const error = e.error as unknown;
        const described = error instanceof Error ? describeReason(error) : { message: e.message || "Script error" };
        enqueue({ kind: "JS_ERROR", ...described });
      } catch {
        // dropped
      }
    };
    const onRejection = (event: Event) => {
      try {
        enqueue({ kind: "UNHANDLED_REJECTION", ...describeReason((event as PromiseRejectionEvent).reason) });
      } catch {
        // dropped
      }
    };
    const onPageHide = () => {
      try {
        if (s.queue.length === 0) return;
        const batches = toBatches(s.queue.splice(0));
        const beacon =
          s.cfg.sendBeacon ??
          ((url: string, body: string) =>
            typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function"
              ? navigator.sendBeacon(url, body)
              : false);
        for (const batch of batches) beacon(s.cfg.endpoint, JSON.stringify({ reports: batch }));
      } catch {
        // dropped
      }
    };

    s.listeners = [
      ["error", onError],
      ["unhandledrejection", onRejection],
      ["pagehide", onPageHide],
    ];
    for (const [type, fn] of s.listeners) window.addEventListener(type, fn);
  } catch {
    // the app runs without a reporter
  }
}

/** Called by the API wrapper for a 5xx answer or a rejected fetch. Never throws. */
export function reportApiFailure(f: {
  apiPath: string;
  httpStatus?: number;
  kind: "API_5XX" | "NETWORK";
  message: string;
}): void {
  try {
    enqueue({
      kind: f.kind,
      message: f.message,
      apiPath: pathOnly(f.apiPath),
      httpStatus: f.httpStatus,
    });
  } catch {
    // dropped
  }
}

export function __resetErrorReporterForTests(): void {
  if (state && typeof window !== "undefined") {
    for (const [type, fn] of state.listeners) window.removeEventListener(type, fn);
  }
  state = null;
}
